import React from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/api";
import { ArrowUpRight, ArrowRight, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";

const LEARN_URL = "https://learn.ravemora.com/";
const WA_RECRUITMENT = `https://wa.me/917483500550?text=${encodeURIComponent(
  "Hi Ravemora Global! I'd like to discuss my recruitment requirements and explore how you can help us build our talent pipeline."
)}`;
const WA_HERO = `https://wa.me/917483500550?text=${encodeURIComponent(
  "Hi Ravemora, I'm interested in your recruitment services. Please share how Ravemora can help us with our hiring requirements."
)}`;

const motif = ["Potential", "Capability", "Opportunity", "Workforce"];

const specialisation = [
  { n: "01", title: "Fresher Recruitment", line: "We specialize in recruitment solutions focused on fresh graduates and entry-level talent, helping organizations build their workforce with emerging professionals." },
  { n: "02", title: "IT Fresher Recruitment", line: "Recruitment of fresh graduates for entry-level IT and technology roles based on specific employer requirements." },
  { n: "03", title: "Trained Fresher Recruitment", line: "Connecting organizations with fresh graduates who have received practical, role-oriented training aligned with workplace requirements." },
  { n: "04", title: "Entry-Level Talent Recruitment", line: "Sourcing and screening candidates for entry-level positions across technology, business and other professional functions." },
  { n: "05", title: "Junior Talent Recruitment", line: "Helping organizations identify candidates for junior and early-career positions across relevant business and technology functions." },
  { n: "06", title: "Campus & Graduate Hiring", line: "Supporting organizations with structured graduate and entry-level hiring requirements." },
];

const approach = [
  { n: "01", title: "Understand", line: "We begin by understanding the organization, role, workforce requirement and capabilities needed for success." },
  { n: "02", title: "Source", line: "We identify and source fresh graduates, entry-level professionals and relevant emerging talent through appropriate talent channels." },
  { n: "03", title: "Assess", line: "We evaluate candidates against the requirements of the role, considering relevant qualifications, skills, communication, practical capability and potential." },
  { n: "04", title: "Connect", line: "We introduce suitable candidates to organizations based on role requirements and candidate alignment." },
  { n: "05", title: "Build", line: "Where required, we can support longer-term talent pipeline development, practical capability building and workforce requirements." },
];

const fresherSignals = [
  "Relevant knowledge", "Practical skills", "Communication", "Learning ability",
  "Problem-solving approach", "Adaptability", "Professional attitude", "Potential for growth",
];

const stages = [
  { title: "Academic Foundation", line: "What candidates know." },
  { title: "Practical Capability", line: "What candidates can apply." },
  { title: "Workplace Readiness", line: "How candidates can contribute in a professional environment." },
];

const solutions = [
  { n: "01", title: "Fresher Recruitment Services", line: "Recruitment support focused on identifying and connecting businesses with qualified fresh graduates and entry-level talent." },
  { n: "02", title: "Entry-Level Talent Recruitment", line: "Sourcing and screening candidates for entry-level positions across technology, business and other professional functions." },
  { n: "03", title: "IT Fresher Recruitment", line: "Recruitment of fresh graduates for entry-level IT and technology roles based on employer requirements." },
  { n: "04", title: "Trained Fresher Recruitment", line: "Connecting organizations with fresh graduates who have received practical, role-oriented training aligned with workplace requirements." },
  { n: "05", title: "Industry-Ready Talent Recruitment", line: "Helping organizations access emerging talent with relevant practical skills and workplace capabilities." },
  { n: "06", title: "Fresher Talent Sourcing", line: "Identifying and sourcing promising fresh graduates and early-career candidates from suitable talent pools." },
  { n: "07", title: "Campus & Graduate Hiring", line: "Supporting organizations with structured graduate and entry-level hiring requirements." },
  { n: "08", title: "Junior Talent Recruitment", line: "Sourcing candidates for junior and early-career positions across relevant business and technology functions." },
  { n: "09", title: "Fresher Talent Pipeline Development", line: "Building a pipeline of emerging talent for organizations with recurring fresher and entry-level hiring needs." },
  { n: "10", title: "Manpower Outsourcing for Freshers", line: "Providing qualified entry-level professionals through flexible manpower outsourcing arrangements." },
];

const beyond = [
  "Junior Talent Recruitment", "Graduate Recruitment", "Entry-Level Talent", "Technology Talent",
  "Business & Professional Talent", "Workforce Pipeline Development", "Manpower Outsourcing",
];

const expectations = [
  { n: "01", title: "Requirement Understanding", line: "We take time to understand the role and the organization's workforce requirement." },
  { n: "02", title: "Structured Talent Sourcing", line: "We identify candidates through relevant and appropriate talent channels." },
  { n: "03", title: "Role-Aligned Assessment", line: "Candidates are considered against the capabilities and requirements relevant to the position." },
  { n: "04", title: "Emerging Talent Focus", line: "We have a particular focus on fresh graduates, entry-level professionals and developing talent." },
  { n: "05", title: "Capability Perspective", line: "We look beyond qualifications to understand practical capability and potential." },
  { n: "06", title: "Long-Term Workforce Thinking", line: "We aim to support sustainable talent pipelines rather than only individual vacancies." },
];

const journey = [
  { title: "Discover", line: "Identify potential." },
  { title: "Develop", line: "Build capability." },
  { title: "Connect", line: "Create opportunity." },
  { title: "Support", line: "Enable workforce growth." },
];

const why = [
  { title: "Human Capital Perspective", line: "We view recruitment as part of a broader workforce strategy." },
  { title: "Fresher Focus", line: "We specialize in connecting organizations with emerging and entry-level talent." },
  { title: "Practical Capability", line: "Where relevant, we consider practical skills alongside academic qualifications." },
  { title: "Structured Approach", line: "Our recruitment process follows a defined approach from requirement understanding through candidate connection." },
  { title: "Talent Pipeline Thinking", line: "We help organizations think beyond one vacancy and build future talent pipelines." },
  { title: "Long-Term Relationships", line: "We aim to build relationships with both organizations and talent beyond a single hiring transaction." },
];

export default function Recruitment() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <div>
      <Seo
        path="/recruitment"
        exactTitle="Recruitment & Staffing Solutions | Ravemora Global"
        image="https://ravemora.com/og/og-recruitment.jpg"
        description="Ravemora Global provides recruitment and staffing solutions to help businesses find the right talent for their evolving workforce needs."
      />

      {/* 1 — HERO */}
      <section className="relative overflow-hidden" data-testid="recruitment-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-6 lg:pt-10 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-navy/60">Talent &amp; Recruitment</div>
            <h1 className="serif-display mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-navy">
              Build Your Workforce <br />
              With <span className="text-gold">Emerging Talent.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-navy/70 leading-relaxed">
              Ravemora Global helps organizations discover, assess and connect with fresh graduates, entry-level professionals and emerging talent for the workforce they are building.
            </p>
            <p className="mt-4 max-w-xl text-navy/60 leading-relaxed">
              From fresher recruitment to talent pipeline development, we focus on finding people with the potential, capabilities and motivation to grow with your organization.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="recruitment-hero-talk"><ArrowRight size={14}/> Talk to Our Talent Team</Link>
              <button onClick={() => scrollTo("recruitment-difference")} className="btn-outline" data-testid="recruitment-hero-approach">Explore Our Approach</button>
              <a href={WA_HERO} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="hero-whatsapp"><MessageCircle size={14}/> WhatsApp Us</a>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] w-full overflow-hidden border border-navy/10">
              <img src={IMAGES.teamMeeting} alt="Talent assessment and professional collaboration at Ravemora Global" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
        {/* Motif — From Potential to Workforce */}
        <div className="border-y border-navy/10" data-testid="recruitment-motif">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center gap-x-8 gap-y-3">
            {motif.map((m, i) => (
              <React.Fragment key={m}>
                <span className={`label-eyebrow ${i === motif.length - 1 ? "text-gold" : "text-navy/50"}`}>{m}</span>
                {i < motif.length - 1 && <span className="text-gold">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* 2 — THE RAVEMORA DIFFERENCE */}
      <section id="recruitment-difference" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 scroll-mt-24" data-testid="recruitment-difference">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <h2 className="serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-navy">
              Recruitment Should Be About More Than <span className="text-gold">Filling a Vacancy.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-6 text-lg leading-relaxed text-navy/80">
              <p>A CV can show qualifications. An interview can reveal communication. But building a strong workforce requires a broader understanding of capability, potential and organizational fit.</p>
              <p>At Ravemora, we approach recruitment as a Human Capital Transformation process.</p>
            </div>
            <p className="mt-8 text-navy/70">We look beyond the immediate vacancy to understand:</p>
            <ul className="mt-4 space-y-3">
              {[
                "What the organization needs",
                "What capabilities the role requires",
                "What potential the candidate demonstrates",
                "What practical skills can be developed",
                "How the candidate can grow within the organization",
              ].map((li) => (
                <li key={li} className="flex items-baseline gap-4 text-navy/80">
                  <span className="text-gold serif-display">—</span> {li}
                </li>
              ))}
            </ul>
            <blockquote className="mt-10 border-l-2 border-gold pl-8">
              <p className="serif-display text-2xl leading-snug text-navy">The objective is not simply to place a candidate. It is to create a meaningful connection between talent and opportunity.</p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* 3 — OUR SPECIALISATION */}
      <section className="bg-navy text-offwhite relative overflow-hidden grain" data-testid="recruitment-specialisation">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 relative z-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-gold">Our specialisation</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight">Where We Create the Most Value</h2>
            <p className="serif-display text-xl md:text-2xl text-gold mt-4">Emerging talent. Practical capability. Long-term potential.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {specialisation.map((s) => (
              <div key={s.n} className="border border-gold/30 p-8" data-testid={`specialisation-${s.n}`}>
                <div className="serif-display text-gold text-2xl leading-none">{s.n}</div>
                <h3 className="serif-display text-2xl mt-5 leading-snug">{s.title}</h3>
                <p className="text-offwhite/70 mt-3 leading-relaxed">{s.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — OUR RECRUITMENT APPROACH */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="recruitment-approach">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-navy/60">Our recruitment approach</div>
          <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">From Vacancy <span className="text-gold">to Capability.</span></h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12 mt-14">
          {approach.map((s) => (
            <div key={s.n} className="border-t border-navy pt-6" data-testid={`approach-${s.n}`}>
              <div className="serif-display text-gold text-4xl leading-none">{s.n}</div>
              <div className="serif-display text-xl text-navy mt-5">{s.title}</div>
              <p className="text-navy/70 mt-3 text-sm leading-relaxed">{s.line}</p>
            </div>
          ))}
        </div>
        <p className="serif-display text-2xl md:text-3xl text-navy mt-16 max-w-3xl leading-snug">
          We move from vacancy-driven hiring to <span className="text-gold">capability-focused talent acquisition.</span>
        </p>
      </section>

      {/* 5 — FRESHER TALENT */}
      <section className="bg-slateBg py-24 lg:py-28" data-testid="recruitment-fresher">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="label-eyebrow text-navy/60">Fresher talent</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Fresh Talent. <span className="text-gold">Serious Potential.</span></h2>
            <p className="serif-display text-xl md:text-2xl text-gold mt-4">Building the bridge between education and employment.</p>
            <p className="mt-8 text-lg text-navy/80 leading-relaxed">
              Fresh graduates represent one of the most important sources of emerging talent for organizations. But hiring freshers requires more than collecting resumes.
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="text-navy/70">Organizations need a structured approach to identifying:</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-6">
              {fresherSignals.map((f) => (
                <div key={f} className="border-t border-gold pt-4">
                  <span className="serif-display text-lg text-navy leading-snug">{f}</span>
                </div>
              ))}
            </div>
            <p className="mt-10 text-lg text-navy/80 leading-relaxed">
              Ravemora focuses on helping organizations identify and connect with emerging talent based on the requirements of the role and the organization.
            </p>
            <blockquote className="mt-8 border-l-2 border-gold pl-8">
              <p className="serif-display text-2xl leading-snug text-navy">We help organizations look beyond experience and recognize potential.</p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* 6 — INDUSTRY-READY TALENT */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="recruitment-industry-ready">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-navy/60">Industry-ready talent</div>
          <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">From Potential to <span className="text-gold">Industry-Ready Capability.</span></h2>
          <p className="mt-6 text-lg text-navy/70 leading-relaxed">
            Some organizations need candidates who can contribute more quickly in practical work environments. Where appropriate, Ravemora can work around role-specific capability development and practical training to help bridge the gap between academic learning and workplace requirements.
          </p>
        </div>
        <div className="grid lg:grid-cols-3 gap-6 mt-14">
          {stages.map((s, i) => (
            <div key={s.title} className="relative border border-navy/10 p-10" data-testid={`stage-${i + 1}`}>
              <div className="label-eyebrow text-gold">Stage {i + 1}</div>
              <h3 className="serif-display text-2xl text-navy mt-4 leading-snug">{s.title}</h3>
              <p className="text-navy/70 mt-3 leading-relaxed">{s.line}</p>
              {i < stages.length - 1 && (
                <span className="hidden lg:block absolute top-1/2 -right-5 -translate-y-1/2 text-gold text-2xl z-10">→</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* 7 — OUR TALENT SOLUTIONS */}
      <section className="bg-slateBg py-24 lg:py-28" data-testid="recruitment-solutions">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-navy/60">Our talent solutions</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Talent Solutions Built Around Your Workforce Needs</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {solutions.map((s) => (
              <div key={s.n} className="bg-offwhite border border-navy/10 p-8" data-testid={`solution-${s.n}`}>
                <div className="serif-display text-gold text-xl leading-none">{s.n}</div>
                <h3 className="serif-display text-xl text-navy mt-4 leading-snug">{s.title}</h3>
                <p className="text-navy/70 mt-3 text-sm leading-relaxed">{s.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8 — BEYOND FRESHERS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="recruitment-beyond">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-navy/60">Beyond freshers</div>
          <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Talent Solutions That Grow With Your Workforce</h2>
          <p className="mt-6 text-lg text-navy/70 leading-relaxed">
            While freshers and emerging professionals are a major focus of Ravemora's recruitment practice, our talent solutions can also support organizations with broader junior and professional workforce requirements.
          </p>
        </div>
        <div className="flex flex-wrap gap-4 mt-12">
          {beyond.map((b) => (
            <div key={b} className="border border-navy/15 px-6 py-4" data-testid={`beyond-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="serif-display text-lg text-navy leading-snug">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 9 — WHAT ORGANISATIONS CAN EXPECT */}
      <section className="bg-slateBg py-24 lg:py-28" data-testid="recruitment-expect">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-navy/60">What organizations can expect</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">A Recruitment Partner Focused on <span className="text-gold">Quality and Alignment</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 mt-14">
            {expectations.map((e) => (
              <div key={e.n} className="border-t border-navy pt-6" data-testid={`expect-${e.n}`}>
                <div className="serif-display text-gold text-2xl leading-none">{e.n}</div>
                <h3 className="serif-display text-2xl text-navy mt-4 leading-snug">{e.title}</h3>
                <p className="text-navy/70 mt-3 leading-relaxed">{e.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10 — HUMAN CAPITAL TRANSFORMATION APPROACH */}
      <section className="bg-navy text-offwhite relative overflow-hidden grain" data-testid="recruitment-journey">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 relative z-10">
          <div className="label-eyebrow text-gold">Human Capital Transformation</div>
          <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight max-w-3xl">Recruitment Is One Part of the <span className="text-gold">Human Capital Journey.</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14 mt-16">
            {journey.map((j, i) => (
              <div key={j.title} className="border-t border-gold pt-8" data-testid={`journey-${j.title.toLowerCase()}`}>
                <div className="serif-display text-gold text-4xl leading-none">0{i + 1}</div>
                <div className="serif-display text-2xl mt-6">{j.title}</div>
                <p className="text-offwhite/70 mt-3 leading-relaxed">{j.line}</p>
              </div>
            ))}
          </div>
          <p className="mt-16 text-lg text-offwhite/80 leading-relaxed max-w-3xl">
            Ravemora Global brings recruitment together with <Link to="/learning" className="text-gold hover:underline">learning</Link>, mentoring and <Link to="/payroll" className="text-gold hover:underline">workforce support</Link> to create a broader Human Capital Transformation ecosystem.
          </p>
          <p className="mt-4 text-offwhite/70 leading-relaxed max-w-3xl">
            <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Learn at Ravemora</a> helps develop practical capability, while Ravemora Recruitment connects emerging talent with organizations.
          </p>
        </div>
      </section>

      {/* 11 — FOR EMPLOYERS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="recruitment-employers">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-navy/60">For employers</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Looking to Build Your <span className="text-gold">Next Talent Pipeline?</span></h2>
            <p className="mt-8 text-lg text-navy/70 leading-relaxed max-w-xl">
              Whether you are hiring your first group of fresh graduates, expanding an entry-level workforce, building an IT talent pipeline or looking for flexible manpower support, Ravemora can work with you to understand the requirement and identify an appropriate talent approach.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={WA_RECRUITMENT} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="employers-discuss"><MessageCircle size={14}/> WhatsApp Us</a>
              <Link to="/contact" className="btn-outline" data-testid="employers-pipeline">Build a Talent Pipeline</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] w-full overflow-hidden border border-navy/10">
              <img src={IMAGES.workforce} alt="Workforce planning and graduate hiring support" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* 12 — FOR CANDIDATES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 lg:pb-28" data-testid="recruitment-candidates">
        <div className="border border-navy/10 bg-slateBg p-10 md:p-14 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="label-eyebrow text-navy/60">For candidates</div>
            <h2 className="serif-display text-3xl md:text-4xl mt-4 leading-tight text-navy">Starting Your Career?</h2>
            <p className="mt-5 text-navy/70 leading-relaxed max-w-2xl">
              Ravemora works with fresh graduates and early-career professionals to connect talent with relevant opportunities. Explore genuine opportunities and develop practical capabilities along the way.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/careers" className="btn-outline inline-flex items-center gap-2" data-testid="candidates-explore">
              Explore Opportunities <ArrowUpRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* 13 — WHY RAVEMORA */}
      <section className="bg-slateBg py-24 lg:py-28" data-testid="recruitment-why">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-navy/60">Why Ravemora</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Why <span className="text-gold">Ravemora?</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 mt-14">
            {why.map((w, i) => (
              <div key={w.title} className="border-t border-navy pt-6" data-testid={`why-${i + 1}`}>
                <div className="serif-display text-gold text-2xl leading-none">0{i + 1}</div>
                <h3 className="serif-display text-2xl text-navy mt-4 leading-snug">{w.title}</h3>
                <p className="text-navy/70 mt-3 leading-relaxed">{w.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 14 — FINAL CTA */}
      <section className="relative overflow-hidden bg-offwhite border-t border-navy/10" data-testid="recruitment-final-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 relative z-10">
          <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-tight text-navy max-w-3xl">Build the Workforce Your <span className="text-gold">Organization Needs.</span></h2>
          <p className="mt-8 max-w-2xl text-lg text-navy/70 leading-relaxed">
            The right talent is not simply about finding people with the right qualifications. It is about connecting capability, potential and opportunity with the needs of the organization.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href={WA_RECRUITMENT} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="recruitment-final-talk"><MessageCircle size={14}/> WhatsApp Us</a>
            <Link to="/contact" className="btn-outline" data-testid="recruitment-final-pipeline">Build Your Talent Pipeline</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
